import { useState, useEffect, useRef } from 'react';
import { Globe, Info, Loader2, Link as LinkIcon, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import type { ExternalCpoConnectionRequestDto, OcpiVersion } from '../../types/api-types';

interface ExternalKioskSetupFormProps {
  onSubmit: (data: ExternalCpoConnectionRequestDto) => Promise<void>;
  onCancel?: () => void;
  onLoadCsmsNames: () => Promise<string[]>;
  isLoading?: boolean;
  error?: string | null;
}

const OCPI_VERSIONS: OcpiVersion[] = ['2.2.1', '2.2', '2.1.1'];

/**
 * Form for setting up Kiosk connection for External CPO operators
 * Collects versions_url, token_a, connection_version, and csms_name (dropdown from GET /ocpi-admin/csms)
 */
export function ExternalKioskSetupForm({
  onSubmit,
  onCancel,
  onLoadCsmsNames,
  isLoading,
  error
}: ExternalKioskSetupFormProps) {
  // Form fields
  const [versionsUrl, setVersionsUrl] = useState('');
  const [tokenA, setTokenA] = useState('');
  const [connectionVersion, setConnectionVersion] = useState<OcpiVersion>('2.2.1');
  const [csmsName, setCsmsName] = useState('');
  const [showTokenA, setShowTokenA] = useState(false);
  const isSubmittingRef = useRef(false);

  const [csmsNames, setCsmsNames] = useState<string[]>([]);
  const [csmsNamesLoading, setCsmsNamesLoading] = useState(false);
  const [csmsNamesError, setCsmsNamesError] = useState<string | null>(null);
  const [csmsRetryKey, setCsmsRetryKey] = useState(0);

  // Validation errors
  const [versionsUrlError, setVersionsUrlError] = useState<string | null>(null);
  const [tokenAError, setTokenAError] = useState<string | null>(null);
  const [csmsNameError, setCsmsNameError] = useState<string | null>(null);

  useEffect(() => {
    // Ignore results of a request superseded by a newer one or by unmount
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    setCsmsNamesLoading(true);
    setCsmsNamesError(null);

    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('timeout')), 10000);
    });

    Promise.race([onLoadCsmsNames(), timeout])
      .then((names) => {
        if (cancelled) return;
        // Radix SelectItem throws on empty-string values
        const validNames = (names ?? []).filter((name) => name?.trim());
        if (validNames.length === 0) {
          setCsmsNamesError('No CSMS available. Please try again later.');
        } else {
          setCsmsNames(validNames);
        }
        setCsmsNamesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCsmsNamesError('Failed to load CSMS list. Please try again later.');
        setCsmsNamesLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [onLoadCsmsNames, csmsRetryKey]);

  const validateVersionsUrl = (value: string): boolean => {
    if (!value.trim()) {
      setVersionsUrlError('Versions URL is required');
      return false;
    }
    try {
      new URL(value.trim());
      setVersionsUrlError(null);
      return true;
    } catch {
      setVersionsUrlError('Must be a valid URL');
      return false;
    }
  };

  const validateTokenA = (value: string): boolean => {
    if (!value.trim()) {
      setTokenAError('Token A is required');
      return false;
    }
    setTokenAError(null);
    return true;
  };

  const validateCsmsName = (value: string): boolean => {
    if (!value.trim()) {
      setCsmsNameError('CSMS selection is required');
      return false;
    }
    setCsmsNameError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;

    const isVersionsUrlValid = validateVersionsUrl(versionsUrl);
    const isTokenAValid = validateTokenA(tokenA);
    const isCsmsNameValid = validateCsmsName(csmsName);

    if (!isVersionsUrlValid || !isTokenAValid || !isCsmsNameValid) {
      return;
    }

    isSubmittingRef.current = true;
    try {
      await onSubmit({
        connection_version: connectionVersion,
        versions_url: versionsUrl.trim(),
        token_a: tokenA.trim(),
        csms_name: csmsName.trim(),
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // CSMS list failed to load — block form submission
  const csmsUnavailable = !csmsNamesLoading && !!csmsNamesError;

  return (
    <Card className="border-purple-200 bg-purple-50/50">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <LinkIcon className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <CardTitle className="text-lg text-purple-900">Setup Kiosk Connection</CardTitle>
            <CardDescription className="text-purple-700">
              Connect your external OCPI system to the Nayax Kiosk network.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
          {/* Information Box */}
          <div className="bg-white rounded-lg border border-purple-200 p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  <strong>Versions URL:</strong> The OCPI versions endpoint URL of your external system (e.g., https://your-system.com/ocpi/versions).
                </p>
                <p>
                  <strong>Token A:</strong> The authentication token for your external OCPI system.
                </p>
                <p>
                  <strong>OCPI Version:</strong> The OCPI protocol version to use for communication.
                </p>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Versions URL */}
            <div className="space-y-2">
              <Label htmlFor="versionsUrl" className="text-gray-700">
                Versions URL <span className="text-red-500">*</span>
              </Label>
              <Input
                id="versionsUrl"
                name="ocpi-versions-url"
                autoComplete="off"
                value={versionsUrl}
                onChange={(e) => {
                  setVersionsUrl(e.target.value);
                  if (versionsUrlError) validateVersionsUrl(e.target.value);
                }}
                placeholder="https://your-system.com/ocpi/versions"
                className={versionsUrlError ? 'border-red-500' : ''}
                disabled={isLoading}
              />
              {versionsUrlError && <p className="text-xs text-red-600">{versionsUrlError}</p>}
              <p className="text-xs text-gray-500">The OCPI versions endpoint of your external system</p>
            </div>

            {/* Token A */}
            <div className="space-y-2">
              <Label htmlFor="tokenA" className="text-gray-700">
                Token A <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="tokenA"
                  name="ocpi-token-a"
                  type={showTokenA ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={tokenA}
                  onChange={(e) => {
                    setTokenA(e.target.value);
                    if (tokenAError) validateTokenA(e.target.value);
                  }}
                  placeholder="Your authentication token"
                  className={`pr-10 ${tokenAError ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowTokenA(!showTokenA)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                >
                  {showTokenA ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {tokenAError && <p className="text-xs text-red-600">{tokenAError}</p>}
              <p className="text-xs text-gray-500">Authentication token for your external OCPI system</p>
            </div>

            {/* Connection Version */}
            <div className="space-y-2">
              <Label htmlFor="connectionVersion" className="text-gray-700">
                OCPI Version <span className="text-red-500">*</span>
              </Label>
              <Select
                value={connectionVersion}
                onValueChange={(value) => setConnectionVersion(value as OcpiVersion)}
                disabled={isLoading}
              >
                <SelectTrigger id="connectionVersion">
                  <SelectValue placeholder="Select OCPI version" />
                </SelectTrigger>
                <SelectContent>
                  {OCPI_VERSIONS.map((version) => (
                    <SelectItem key={version} value={version}>
                      OCPI {version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">The OCPI protocol version to use</p>
            </div>

            {/* CSMS — dropdown from GET /ocpi-admin/csms */}
            <div className="space-y-2">
              <Label htmlFor="kiosk-csmsName" className="text-gray-700">
                CSMS <span className="text-red-500">*</span>
              </Label>

              {/* Loading state */}
              {csmsNamesLoading && (
                <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-gray-50">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  <span className="text-sm text-gray-400">Loading CSMS list...</span>
                </div>
              )}

              {/* Error / unavailable state */}
              {csmsUnavailable && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700 flex-1">{csmsNamesError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCsmsRetryKey((key) => key + 1)}
                    className="h-7"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {/* Dropdown — only shown when names loaded successfully */}
              {!csmsNamesLoading && !csmsUnavailable && (
                <Select
                  value={csmsName}
                  onValueChange={(value) => {
                    setCsmsName(value);
                    if (csmsNameError) setCsmsNameError(null);
                  }}
                  disabled={isLoading}
                >
                  <SelectTrigger id="kiosk-csmsName" className={csmsNameError ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select a CSMS" />
                  </SelectTrigger>
                  <SelectContent>
                    {csmsNames.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {csmsNameError && <p className="text-xs text-red-600">{csmsNameError}</p>}
              <p className="text-xs text-gray-500">The CSMS to associate with this connection</p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isLoading || csmsNamesLoading || csmsUnavailable} className="bg-purple-600 hover:bg-purple-700 text-white">
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2" />
                  Setup Kiosk Connection
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
